import React, { useState, useMemo } from "react";
import { useSkinsMasterContext } from "./SkinsMaster.jsx";
import { gets_skinurl, rarity_colors } from "./dependencies.jsx";
import { twMerge } from "tailwind-merge";
import { Filter } from "lucide-react";
import { TokenIcon } from "../../components/utilityComps.jsx";
import { StoreIndividualDetails } from "./StoreIndividualDetails.jsx";
import { StoreItemSkeleton } from "./StoreItemSkeleton.jsx";

export const StoreIndividualContext = React.createContext({});
export const useStoreIndividualContext = () => React.useContext(StoreIndividualContext);

export const StoreIndividual = () => {
  const { store_indis, qo_skin_instore } = useSkinsMasterContext();
  const is_loading = qo_skin_instore?.isLoading || !store_indis;

  const [filter_rvmode, set_filter_rvmode] = useState("all");
  const [filter_rarity, set_filter_rarity] = useState("all");
  const [search_text, set_search_text] = useState("");
  const [filters_expanded, set_filters_expanded] = useState(false);
  const [selected_item, set_selected_item] = useState(null);

  // Generate dynamic rvmode filter options from store_indis
  const available_rvmodes = useMemo(() => {
    if (!store_indis) return [];
    const rvmodes = store_indis
      .filter(item => item.active)
      .map(item => item.rvmode);
    return ["all", ...new Set(rvmodes)];
  }, [store_indis]);

  // Generate dynamic rarity filter options from store_indis
  const available_rarities = useMemo(() => {
    if (!store_indis) return [];
    const rarities = store_indis
      .filter(item => item.active)
      .map(item => item.rarity);
    return ["all", ...new Set(rarities)];
  }, [store_indis]);

  // Filter skins based on selected filters
  const filtered_skins = useMemo(() => {
    if (!store_indis) return [];

    let skins = store_indis.filter(item => item.active);

    // Filter by search text
    if (search_text.length >= 2) {
      const search_lower = search_text.toLowerCase();
      skins = skins.filter(item =>
        item.name.toLowerCase().includes(search_lower) ||
        item.id.toLowerCase().includes(search_lower)
      );
    }

    // Filter by rvmode
    if (filter_rvmode !== "all") {
      skins = skins.filter(item => item.rvmode === filter_rvmode);
    }

    // Filter by rarity
    if (filter_rarity !== "all") {
      skins = skins.filter(item => item.rarity === filter_rarity);
    }

    return skins;
  }, [store_indis, filter_rvmode, filter_rarity, search_text]);

  const store_individual_con = {
    filter_rvmode,
    set_filter_rvmode,
    filter_rarity,
    set_filter_rarity,
    search_text,
    set_search_text,
    filters_expanded,
    set_filters_expanded,
    selected_item,
    set_selected_item,
    filtered_skins,
    available_rvmodes,
    available_rarities,
  };

  return (
    <StoreIndividualContext.Provider value={store_individual_con}>
      <div className="w-full">
        {/* Show Details if item is selected */}
        {selected_item ? (
          <StoreIndividualDetails />
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {/* Search Bar with Filter Button */}
            <div className="flex gap-2 sm:gap-3">
        <div className="flex-1">
          <input
            type="text"
            value={search_text}
            onChange={(e) => set_search_text(e.target.value)}
            placeholder="Search by name or ID..."
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-400 focus:outline-none focus:border-r3reg focus:ring-1 focus:ring-r3reg/30 transition-all duration-200"
          />
          {search_text.length > 0 && search_text.length < 2 && (
            <p className="text-xs text-slate-400 mt-1">Type at least 2 characters to search</p>
          )}
        </div>

        <button
          onClick={() => set_filters_expanded(!filters_expanded)}
          className={twMerge(
            "px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center",
            filters_expanded
              ? "bg-r3reg text-white shadow-lg shadow-r3reg/30"
              : "bg-slate-800 border border-slate-600 text-slate-300 hover:bg-slate-700 hover:border-r3reg/50"
          )}
        >
          <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </div>

      {/* Collapsible Filters */}
      {filters_expanded && (
        <div className="bg-r3dark/20 border border-r3lig/30 backdrop-blur-md rounded-2xl p-4 sm:p-6">
          <div className="space-y-3 sm:space-y-4">
            {/* Vehicle Type Filter */}
            <div>
              <h4 className="text-xs sm:text-sm font-medium text-gray-300 mb-2">Vehicle Type</h4>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {available_rvmodes.map((rvmode) => (
                  <button
                    key={rvmode}
                    onClick={() => set_filter_rvmode(rvmode)}
                    className={twMerge(
                      "px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-200",
                      filter_rvmode === rvmode
                        ? "bg-r3reg text-white shadow-lg shadow-r3reg/30"
                        : "bg-r3dark/20 text-gray-300 border border-r3lig/30 hover:bg-r3lig/20 hover:text-white"
                    )}
                  >
                    {rvmode === "all" ? "All" : rvmode.charAt(0).toUpperCase() + rvmode.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Rarity Filter */}
            <div>
              <h4 className="text-xs sm:text-sm font-medium text-gray-300 mb-2">Rarity</h4>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {available_rarities.map((rarity) => (
                  <button
                    key={rarity}
                    onClick={() => set_filter_rarity(rarity)}
                    className={twMerge(
                      "px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-200",
                      filter_rarity === rarity
                        ? "bg-r3reg text-white shadow-lg shadow-r3reg/30"
                        : "bg-r3dark/20 text-gray-300 border border-r3lig/30 hover:bg-r3lig/20 hover:text-white"
                    )}
                    style={{
                      backgroundColor: filter_rarity === rarity
                        ? undefined
                        : rarity === "all"
                          ? undefined
                          : `${rarity_colors[rarity] || rarity_colors.common}20`
                    }}
                  >
                    {rarity === "all" ? "All" : rarity.charAt(0).toUpperCase() + rarity.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Count - Only show when actively filtering */}
      {(search_text.length >= 2 || filter_rvmode !== "all" || filter_rarity !== "all") && (
        <div className="text-center text-gray-400 text-xs sm:text-sm">
          Showing {filtered_skins.length} individual skin{filtered_skins.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Skins Grid */}
      {is_loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          {Array.from({ length: 6 }).map((_, idx) => (
            <StoreItemSkeleton key={idx} />
          ))}
        </div>
      ) : filtered_skins.length === 0 ? (
        <div className="text-center text-gray-400 py-6 sm:py-8 text-sm sm:text-base">
          <p>No skins match the selected filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          {filtered_skins.map((item) => {
            return (
              <div
                key={item.id}
                className="bg-r3dark/20 border border-r3lig/30 rounded-xl overflow-hidden hover:border-r3lig/50 transition-all duration-200"
              >
                {/* Skin Image */}
                <div className="aspect-[1166/656] relative overflow-hidden bg-gradient-to-br from-r3dark/40 to-r3lig/20">
                  <img
                    src={gets_skinurl(item.name, item.rvmode, true)}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />

                  {/* Rarity Badge */}
                  <div className="absolute top-3 left-3">
                    <span
                      className="px-2 py-1 rounded-full text-xs font-medium text-white"
                      style={{
                        backgroundColor: rarity_colors[item.rarity] || rarity_colors.common,
                      }}
                    >
                      {item.rarity}
                    </span>
                  </div>

                  {/* Vehicle Type Badge */}
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-black/60 text-white">
                      {item.rvmode}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-3 sm:p-4">
                  <h4 className="text-base sm:text-lg font-digi text-white mb-2 truncate">
                    {item.name}
                  </h4>

                  {/* Price */}
                  <div className="mb-3 sm:mb-4 flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4">
                    {/* USD Pricing (WETH/DEZ) */}
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <div className="flex -space-x-1">
                        <TokenIcon token="WETH" size="md" className="z-10" />
                        <TokenIcon token="DEZ" size="md" className="z-0" />
                      </div>
                      <span className="text-sm sm:text-base font-bold text-white">
                        ${item.price_usd}
                      </span>
                    </div>

                    {/* OR Divider */}
                    <div className="hidden sm:flex relative items-center justify-center w-12 h-6">
                      <div className="absolute top-1 left-0 w-5 h-[1px] bg-gray-400 transform rotate-45 origin-left"></div>
                      <div className="absolute bottom-1 left-0 w-5 h-[1px] bg-gray-400 transform rotate-45 origin-left"></div>
                      <div className="text-sm text-gray-300 font-medium">OR</div>
                    </div>
                    <div className="sm:hidden text-xs text-gray-400">or</div>

                    {/* SGC Pricing */}
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <TokenIcon token="SGC" size="md" />
                      <span className="text-sm sm:text-base font-bold text-r3reg">
                        {item.price_sgc} SGC
                      </span>
                    </div>
                  </div>

                  {/* Purchase Button - Navigate to Details */}
                  <button
                    onClick={() => set_selected_item(item)}
                    className="w-full py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 bg-r3reg hover:bg-r3dark text-white shadow-lg"
                  >
                    View Details
                  </button>
                </div>
              </div>
            );
          })}
            </div>
          )}
        </div>
      )}
      </div>
    </StoreIndividualContext.Provider>
  );
};